import { readonly, ref } from 'vue';

import { toUrlObjectOrNull } from '@/utils/transformer/url';

import { usePostMessageReceiver } from '../composables/usePostMessageReceiver';

const REQUEST_TYPES = Object.freeze({
  PIP_REQUEST: 'PIP_REQUEST',
  AUDIO_REQUEST: 'AUDIO_REQUEST',
  PLAY_REQUEST: 'PLAY_REQUEST',
  SEEK_REQUEST: 'SEEK_REQUEST',
  ADD_TO_CART_NOTIFICATION_REQUEST: 'ADD_TO_CART_NOTIFICATION_REQUEST',
  OPEN_SHARING_REQUEST: 'OPEN_SHARING_REQUEST',
});

const iframeEl = ref<HTMLIFrameElement | null>(null);

/**
 * @param el
 * @remarks 若這個 function 需要 export 出去，要注意重複 bind 的問題
 */
const bindStreamIframeEl = (el: HTMLIFrameElement) => {
  if (!(el instanceof HTMLIFrameElement)) {
    throw new Error('parameter[0] is not HTMLIFrameElement');
  }
  iframeEl.value = el;
};

const postMessage = (type: string, data: unknown) => {
  try {
    const el = iframeEl.value;
    if (!el) { throw new Error('No iframeEl found'); }

    const url = toUrlObjectOrNull(el.src);
    if (!url) { throw new Error(`Non-parsable element src: ${el.src}`); }

    el.contentWindow?.postMessage({ type, data }, url.origin);
  } catch (error) {
    console.error(error);
  }
};

const { onStreamReady, onVideoElBoundStatusChanged } = usePostMessageReceiver();

const canStreamReadyToPostMessages = ref(false);
export const canStreamPostMessages = readonly(canStreamReadyToPostMessages);

// REF: https://stackoverflow.com/a/50457849
// NOTE: 如果有多個 buyer iframe 還是會有問題，但目前不考慮此情況
const getFrameElByEvent = (event: MessageEvent) => {
  return Array.from(document.querySelectorAll('iframe'))
    .filter((iframe) => iframe.contentWindow === event.source)[0];
};

onStreamReady((event: MessageEvent) => {
  const el = getFrameElByEvent(event);
  bindStreamIframeEl(el);
  canStreamReadyToPostMessages.value = true;
});

let isVideoElBound = false;
const taskQueueBeforeVideoElBound: (() => void)[] = [];
onVideoElBoundStatusChanged((isBound: boolean) => {
  isVideoElBound = isBound;

  if (isVideoElBound) {
    for (const taskFn of taskQueueBeforeVideoElBound) {
      taskFn();
    }
  }
});

/** @param requestToPip */
export const requestPip = (requestToPip: boolean) => {
  postMessage(REQUEST_TYPES.PIP_REQUEST, requestToPip);
};

/** @param requestToMute */
export const requestMute = (requestToMute: boolean) => {
  const makeVideoRequest = () => postMessage(REQUEST_TYPES.AUDIO_REQUEST, requestToMute);

  if (isVideoElBound) {
    makeVideoRequest();
  } else {
    taskQueueBeforeVideoElBound.push(makeVideoRequest);
  }
};

/** @param requestToPlay */
export const requestPlay = (requestToPlay: boolean) => {
  const makeVideoRequest = () => postMessage(REQUEST_TYPES.PLAY_REQUEST, requestToPlay);

  if (isVideoElBound) {
    makeVideoRequest();
  } else {
    taskQueueBeforeVideoElBound.push(makeVideoRequest);
  }
};

/** @param seekTime */
export const requestSeek = (seekTime: number) => {
  const makeVideoRequest = () => postMessage(REQUEST_TYPES.SEEK_REQUEST, seekTime);

  if (isVideoElBound) {
    makeVideoRequest();
  } else {
    taskQueueBeforeVideoElBound.push(makeVideoRequest);
  }
};

/** @param productId */
export const requestAddToCartNotification = (productId: number) => {
  postMessage(REQUEST_TYPES.ADD_TO_CART_NOTIFICATION_REQUEST, productId);
};

/** @param seconds */
export const requestOpenSharing = (seconds = 0) => {
  const makeVideoRequest = () => postMessage(REQUEST_TYPES.OPEN_SHARING_REQUEST, seconds);

  if (isVideoElBound) {
    makeVideoRequest();
  } else {
    taskQueueBeforeVideoElBound.push(makeVideoRequest);
  }
};
